<template>
  <k-panel
    title="Project Competencies"
    :hasContentToggle="false"
    >
    <template #body>
      <div class="competencies-content">
        <div class="competencies-layout" :class="{ 'with-table': competencyDescriptionsVisible }">
          <div class="competencies-left">
            <div class="competencies-info">
              <p>Ensure all listed competencies are met by the submitted evidence.</p>
              <p>
                Competencies met by evidence approved by your mentor are listed as approved.
                <a href="#"
                class="competency-descriptions-link"
                @click.prevent="$emit('toggle-descriptions')">
                Competency descriptions
                <i :class="chevronClass"></i>
                </a>
              </p>
            </div>

            <div class="competencies-badges">
              <div class="competency-pills">
                <span
                  v-for="competency in visibleCompetencies"
                  :key="competency.id"
                  :class="getCompetencyClass(competency)"
                  class="competency-pill"
                >
                  {{ competency.label }}
                </span>
              </div>
              <div v-if="shouldShowToggleButton" class="toggle-container">
                <button
                  class="show-more-btn"
                  @click="toggleShowAll"
                  >
                  {{ showAll ? 'Show less' : `+ ${hiddenCompetencies.length} more` }}
                  <i :class="showAll ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                </button>
              </div>
            </div>
          </div>

          <div v-if="competencyDescriptionsVisible" class="competencies-right">
            <div class="competency-table-container">
              <k-table
                :headers="competencyTableHeaders"
                :rows="competencyTableRows"
                :hideable="false"
                :downloadable="false"
                :panel="false"
                :max="5"
                class="competency-descriptions-table"
              />
              </div>
          </div>
        </div>
      </div>
    </template>
  </k-panel>
</template>

<style scoped>
.competencies-content {
  padding: 15px 0;
}

.competencies-layout {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

.competencies-left {
  flex: 1;
  min-width: 0;
}

.competencies-right {
  flex: 1;
  min-width: 400px;
}

.competencies-info {
  margin-bottom: 25px;
}

.competencies-info p {
  margin-bottom: 10px;
  color: var(--kate-type-primary);
}

.competency-descriptions-link {
  color: var(--kate-type-link);
  text-decoration: none;
  cursor: pointer;
}

.competency-descriptions-link:hover {
  text-decoration: underline;
}

.competency-descriptions-link i {
  margin-left: 5px;
  font-size: 0.8em;
}

.competencies-badges {
  margin-top: 20px;
}

.competency-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.competency-pill {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  border: 2px solid;
  transition: all 0.2s ease;
  cursor: default;
}

.competency-pill.approved {
  color: #000;
  background-color: var(--kate-success);
  border-color: var(--kate-success);
}

.competency-pill.pending {
  background-color: #f8f9fa;
  color: #000;
  border-color: #000;
}

.show-more-btn {
  background: none;
  border: none;
  color: var(--kate-type-link);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.show-more-btn:hover {
  background-color: var(--kate-background-alt);
  text-decoration: underline;
}

.show-more-btn i {
  font-size: 12px;
}

.competency-descriptions-table .k-table-wrapper {
  border: 1px solid var(--kate-border);
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--kate-background-body);
}

.competency-descriptions-table .k-table-header {
  background-color: var(--kate-background-alt);
  font-weight: 600;
  padding: 12px 16px;
  border-bottom: 1px solid var(--kate-border);
  font-size: 14px;
}

.competency-descriptions-table .k-table-cell {
  padding: 12px 16px;
  vertical-align: top;
  font-size: 14px;
  border-bottom: 1px solid var(--kate-border-light, #f1f3f4);
}

.competency-descriptions-table .k-table-row:last-child .k-table-cell {
  border-bottom: none;
}

.competency-descriptions-table .k-table-key-label {
  font-weight: 600;
  color: var(--kate-type-primary);
  background-color: var(--kate-background-alt, #f8f9fa);
  border-radius: 4px;
  padding: 4px 8px;
  display: inline-block;
  min-width: 40px;
  text-align: center;
}

.competency-descriptions-table .k-table-key-category {
  color: var(--kate-type-secondary);
  font-weight: 500;
  text-transform: capitalize;
}

.competency-descriptions-table .k-table-key-description {
  line-height: 1.5;
  color: var(--kate-type-primary);
}

@media (max-width: 768px) {
  .competencies-layout {
    flex-direction: column;
    gap: 20px;
  }

  .competencies-right {
    min-width: 0;
    max-width: 100%;
  }

  .competency-pills {
    grid-template-columns: repeat(auto-fit, minmax(60px, max-content));
    gap: 8px;
  }
}

@media (max-width: 1200px) {
  .competencies-layout.with-table {
    gap: 15px;
  }

  .competencies-layout.with-table .competency-pills {
    grid-template-columns: repeat(auto-fit, minmax(60px, max-content));
    gap: 6px;
  }
}
</style>
<style>
.competency-descriptions-table .k-table-cell {
  padding: 12px 16px;
  vertical-align: top;
  font-size: 14px;
  border: 2px solid var(--kate-border);
  border-radius: 4px;
}

.table-competency-pill {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
  min-width: 36px;
  text-align: center;
  background-color: var(--kate-background-alt, #f8f9fa);
  color: var(--kate-type-primary);
}

.competency-descriptions-table.k-table-container tbody tr:nth-child(2n) {
  background-color: transparent !important;
}
</style>
<script>
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';

export default {
  name: 'ProjectEvidenceCompetencies',
  components: {
    KPanel,
    KTable,
  },
  props: {
    competencies: {
      type: Array,
      required: true,
      default: () => [],
    },
    competencyDescriptionsVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showAll: false,
    };
  },

  computed: {
    visibleCompetencies() {
      const maxVisible = this.competencyDescriptionsVisible ? 8 : 10;
      return this.showAll ? this.competencies : this.competencies.slice(0, maxVisible);
    },
    hiddenCompetencies() {
      const maxVisible = this.competencyDescriptionsVisible ? 8 : 10;
      return this.showAll ? [] : this.competencies.slice(maxVisible);
    },
    shouldShowToggleButton() {
      const maxVisible = this.competencyDescriptionsVisible ? 8 : 10;
      return this.competencies.length > maxVisible;
    },
    chevronClass() {
      return this.competencyDescriptionsVisible
        ? 'fas fa-angle-up'
        : 'fas fa-angle-down';
    },
    competencyTableHeaders() {
      return {
        label: {
          name: 'Label',
          type: 'html',
          sortable: true,
          filterable: true,
        },
        category: {
          name: 'Category',
          type: 'string',
          sortable: true,
          hasDropdown: true,
        },
        description: {
          name: 'Description',
          type: 'string',
          sortable: false,
          filterable: true,
        },
      };
    },
    competencyTableRows() {
      return this.competencies.map(competency => ({
        label: `<span class="table-competency-pill">${competency.label}</span>`,
        category: competency.category,
        description: competency.description,
      }));
    },
  },

  methods: {
    toggleShowAll() {
      this.showAll = !this.showAll;
    },
    getCompetencyClass(competency) {
      return competency.approved ? 'approved' : 'pending';
    },
  },
};
</script>
